import { isGspChannelSessionActiveForProduct } from '@theme/gsp-channel-session';

// Change text only in recognized Rapid Search product results. The app retains
// ownership of every element, class, layout, link and localized "from" label.
const selector = '.rps_predictive_product[data-handle][id^="product-"]';
const requests = new Map();
const pending = new WeakSet();
const originals = new Map();

function setText(node, value) {
  if (!node || node.textContent === value) return;
  const previous = originals.get(node);
  originals.set(node, {
    original: previous && node.textContent === previous.applied ? previous.original : node.textContent,
    applied: value,
    card: node.parentElement.closest(selector),
  });
  node.textContent = value;
}

function restore(card) {
  for (const [node, state] of originals) {
    if (!node.isConnected || state.card === card) {
      if (node.isConnected && node.textContent === state.applied) node.textContent = state.original;
      originals.delete(node);
    }
  }
}

function getPricing(handle) {
  if (!requests.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', 'rapid-search-pricing');
    requests.set(handle, fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Price section unavailable');
        return response.text();
      })
      .then((html) => {
        const result = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-rapid-pricing-product]');
        if (!result) throw new Error('Price section missing');
        return result;
      })
      .catch((error) => {
        requests.delete(handle);
        throw error;
      }));
  }
  return requests.get(handle);
}

async function updateCard(card) {
  const id = card.id.match(/^product-(\d+)$/)?.[1];
  const handle = card.dataset.handle;
  if (!id || !handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return;
  if (!isGspChannelSessionActiveForProduct(id)) {
    restore(card);
    return;
  }
  const price = card.querySelector('.rps_final_price .money');
  const compare = card.querySelector('.rps_original_price .money');
  const badge = card.querySelector('.rps_predictive_discounted_price');
  if (!price || price.closest(selector) !== card || pending.has(card)) return;
  pending.add(card);
  try {
    const result = await getPricing(handle);
    if (!card.isConnected || card.dataset.handle !== handle || card.id !== `product-${id}` ||
        !price.isConnected || !isGspChannelSessionActiveForProduct(id)) return;
    if (result.dataset.rapidPricingProduct !== id || result.dataset.rapidPricingHandle !== handle ||
        result.dataset.rapidPricingEligible !== 'true') return;
    const amount = result.querySelector('[data-rapid-price]')?.textContent.trim();
    const comparison = result.querySelector('[data-rapid-compare]')?.textContent.trim();
    const savings = result.querySelector('[data-rapid-badge]')?.textContent.trim();
    // Unsupported markup is left untouched instead of manufacturing a new layout.
    if (!amount || (compare && !comparison) || (badge && !savings)) return;
    setText(price, amount);
    if (compare) setText(compare, comparison);
    if (badge) setText(badge, savings);
  } catch {
    // Failed requests preserve the app's original display.
  } finally {
    pending.delete(card);
  }
}

let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    for (const [node] of originals) if (!node.isConnected) originals.delete(node);
    document.querySelectorAll(selector).forEach(updateCard);
  });
}
new MutationObserver(schedule).observe(document.body, {
  childList: true, subtree: true, characterData: true,
  attributes: true, attributeFilter: ['data-handle', 'id'],
});
window.addEventListener('pageshow', schedule);
document.addEventListener('visibilitychange', schedule);
setInterval(schedule, 60000);
schedule();
